import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchTemplates } from '../../api/templates';
import TemplateCard from './TemplateCard';
import client from '../../api/client';
import styles from './TemplatesPage.module.css';

const DEFAULT_KEY = 'teachio-default-template';

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function TemplatesPage() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [defaultTemplateId, setDefaultTemplateId] = useState(
    () => localStorage.getItem(DEFAULT_KEY) || null
  );

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const result = await fetchTemplates();
        if (cancelled) return;
        const list = Array.isArray(result) ? result : (result.data || []);
        setTemplates(list);

        // If no default is set, use the first template
        if (!localStorage.getItem(DEFAULT_KEY) && list.length > 0) {
          const firstId = list[0].templateId;
          localStorage.setItem(DEFAULT_KEY, firstId);
          setDefaultTemplateId(firstId);
        }
      } catch (err) {
        if (!cancelled) setError('Failed to load templates. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, []);

  const handleSetDefault = (template) => {
    localStorage.setItem(DEFAULT_KEY, template.templateId);
    setDefaultTemplateId(template.templateId);
  };

  const handleEdit = (template) => {
    navigate(`/templates/${template.templateId}/edit`);
  };

  const handleDelete = async (template) => {
    if (templates.length <= 1) {
      setError('Cannot delete the only template');
      return;
    }
    const confirmed = window.confirm(
      `Delete "${template.name}"? This cannot be undone.`
    );
    if (!confirmed) return;

    try {
      await client.delete(`/api/templates/${template.templateId}`, {
        headers: getAuthHeaders(),
      });
      setTemplates((prev) =>
        prev.filter((t) => t.templateId !== template.templateId)
      );
      // If the deleted template was the default, clear it
      if (defaultTemplateId === template.templateId) {
        localStorage.removeItem(DEFAULT_KEY);
        setDefaultTemplateId(null);
      }
    } catch (err) {
      setError('Failed to delete template. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className={styles.pageLoading}>
        <div className={styles.pageSpinner} />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>My Templates</h1>
        <button
          className={styles.createButton}
          onClick={() => navigate('/templates/new')}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <line x1="8" y1="2" x2="8" y2="14" />
            <line x1="2" y1="8" x2="14" y2="8" />
          </svg>
          New Template
        </button>
      </div>

      {error && <div className={styles.errorBanner}>{error}</div>}

      <div className={styles.grid}>
        {templates.map((template) => (
          <TemplateCard
            key={template.templateId}
            template={template}
            isDefault={defaultTemplateId === template.templateId}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onSetDefault={handleSetDefault}
          />
        ))}

        <button
          className={styles.createCard}
          onClick={() => navigate('/templates/new')}
        >
          <svg
            width="28"
            height="28"
            viewBox="0 0 28 28"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <line x1="14" y1="6" x2="14" y2="22" />
            <line x1="6" y1="14" x2="22" y2="14" />
          </svg>
          <span>Create New Template</span>
        </button>
      </div>
    </div>
  );
}
